'use client';

import { RefreshIcon } from '@/icons';
import { useTouchAvailable } from '@/lib/hooks';
import { AuthorityInfo, Menu } from '@/lib/types';
import { AUTHORITY_CODE } from '@/lib/utils';
import { CustomLink, SocialMediaIcon } from '@/ui';
import { ViewCounter } from '@/widgets/view-counter';
import { Container, Flex, Grid, Skeleton } from '@mantine/core';
import clsx from 'clsx';
import { useTranslations } from 'next-intl';
import Image from 'next/image';
import useSWR from 'swr';
import Links from './Links';
import classnames from './footer.module.css';

type Props = {
  locale: string;
};

export function Footer({ locale }: Props) {
  const t = useTranslations();
  const isTouchAvailable = useTouchAvailable();

  const { data: info, isLoading } = useSWR<AuthorityInfo>(['/authorities/main/authority-info']);

  const { data: links } = useSWR<Menu[]>(['/authorities/main/get-footer-menu']);

  return isLoading ? (
    <Skeleton h={380} />
  ) : (
    <footer>
      <Container>
        <Links links={links || []} />
        <div className="flex flex-col justify-center gap-[2rem] pt-[2rem]">
          <div className={classnames.grid_root_replace}>
            <div className={classnames.grid_inner_container_replace}>
              <div className={clsx(classnames.grid_col_replace, classnames.grid_col_1_replace)}>
                <div className="flex flex-col justify-center items-center lg:items-start gap-[0.75rem]">
                  <CustomLink
                    href="/"
                    className="font--md font-medium uppercase max-w-xl lg:mx-auto lg:block lg:text-center lg:capitalize"
                  >
                    {info?.title}
                  </CustomLink>
                  <p className="font--sm">{info?.address}</p>
                </div>
              </div>
              <div className={clsx(classnames.grid_col_replace, classnames.grid_col_2_replace)}>
                <div className="flex flex-col justify-center items-center gap-[1.25rem]">
                  <CustomLink
                    href={`https://uzinfocom.uz/${locale != 'qr' && locale != 'oz' ? locale : 'uz'}`}
                    target="_blank"
                    className="w-max mx-auto"
                  >
                    <Image src="/img/UZINFOCOM.svg" alt="UZINFOCOM" width={152} height={16} />
                  </CustomLink>
                  <p className="font--sm text-center max-w-xs">
                    © 2001-{new Date().getFullYear()} {t('Barcha huquqlar himoyalangan')}
                  </p>
                </div>
              </div>
              <div className={clsx(classnames.grid_col_replace, classnames.grid_col_3_replace)}>
                <div className="flex flex-col justify-center items-end xl:items-center gap-[0.75rem]">
                  <Flex justify="center" gap="md">
                    {info?.socials?.value && info?.socials?.value?.length > 0
                      ? info?.socials?.value?.map((social) => (
                          <SocialMediaIcon
                            key={social?.type}
                            url={social?.link}
                            type={social?.type}
                            variant="primary"
                            circle
                          />
                        ))
                      : null}
                  </Flex>
                  <div id="www_counter_view" data-counter-id={AUTHORITY_CODE === 'argos' ? '44433' : '536'} />
                </div>
              </div>
            </div>
          </div>
          {info?.last_updated_date ? (
            <div className="flex justify-center items-center gap-[0.75rem]">
              <RefreshIcon />
              <p className="font--sm text-center">
                {t('Oxirgi yangilanish')}: {info?.last_updated_date}
              </p>
            </div>
          ) : null}
          <ViewCounter />
          <p className="font--sm text-center">
            {isTouchAvailable
              ? t(
                  'Diqqat! Agar siz matnda xatoliklarni aniqlasangiz, ma’muriyatni xabardor qilish uchun ularni belgilab, pastda hosil bo‘ladigan tugmani bosing'
                )
              : t(
                  "Diqqat! Agar siz matnda xatoliklarni aniqlasangiz, ularni belgilab, ma'muriyatni xabardor qilish uchun Ctrl+Enter tugmalarini bosing"
                )?.replace('Ctrl', 'Ctrl/Command')}
          </p>
        </div>
      </Container>
    </footer>
  );
}
